import { SmartToy } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveHistoryId, saveConversation, setSize, toggleOpen } from 'store/entities/chatbot/actions';
import { selectActiveHistoryId, selectFullScreen, selectOpened, selectSize } from 'store/entities/chatbot/selectors';
import { useLazyGetWelcomeMessageQuery, useSendMessageMutation } from 'store/services/core/chatbot';
import type { AssistantResponse } from 'types';
import type { ChatHistory, ConversationMeta, Message as MessageType } from 'types/chatbot';
import { getAssistantHistoryScope } from 'utils/chatbotHistory';
import { Footer } from './Footer';
import { Header } from './Header';
import { Message } from './Message';
import { Dialog, Messages, Resizer, StyledChatIconButton } from './index.styles';

const MIN_WIDTH = 320;
const MIN_HEIGHT = 360;
const COPY_FEEDBACK_DURATION_MS = 2000;
type ToggleButtonTestId = 'admin-toolbar-chatbot-button' | 'user-toolbar-chatbot-button';

interface ChatbotWidgetProps {
  assistants: readonly AssistantResponse[];
  historyScope: string;
  isAssistantsLoading: boolean;
  showAssistantPicker: boolean;
  toggleButtonTestId: ToggleButtonTestId;
  fallbackAssistantId?: string;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getNestedErrorDetail = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return undefined;

  const { data } = error;
  if (typeof data !== 'object' || data === null || !('ErrorDetails' in data)) return undefined;

  const { ErrorDetails: errorDetails } = data;
  if (typeof errorDetails !== 'object' || errorDetails === null || !('Detail' in errorDetails)) return undefined;

  return typeof errorDetails.Detail === 'string' ? errorDetails.Detail : undefined;
};

const resolveAssistantId = (
  assistantId: string | null,
  assistants: readonly AssistantResponse[],
  fallbackAssistantId?: string
): string | undefined => {
  if (assistantId !== null && assistants.some((assistant) => assistant.id === assistantId)) return assistantId;

  return fallbackAssistantId !== undefined && assistants.some((assistant) => assistant.id === fallbackAssistantId) ? fallbackAssistantId : undefined;
};

const ChatbotWidgetBase = ({
  assistants,
  historyScope,
  isAssistantsLoading,
  showAssistantPicker,
  toggleButtonTestId,
  fallbackAssistantId,
}: ChatbotWidgetProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [conversation, setConversation] = useState<ConversationMeta>({ assistantId: null });
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [input, setInput] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const visible = useSelector(selectOpened);
  const fullScreen = useSelector(selectFullScreen);
  const size = useSelector(selectSize);
  const activeHistoryId = useSelector(selectActiveHistoryId);
  const historyAssistantId = conversation.assistantId;
  const assistantHistoryScope = getAssistantHistoryScope(historyScope, historyAssistantId);

  const [localSize, setLocalSize] = useState(size);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const requestVersionRef = useRef(0);

  const [getWelcomeMessage] = useLazyGetWelcomeMessageQuery();
  const [sendChatMessage, { isLoading: sending }] = useSendMessageMutation();

  useEffect(() => {
    setLocalSize(size);
  }, [size]);

  useEffect(() => {
    const nextAssistantId = assistants[0]?.id ?? null;
    const isCurrentAssistantAvailable = assistants.some((assistant) => assistant.id === conversation.assistantId);

    if (!isCurrentAssistantAvailable && conversation.assistantId !== nextAssistantId) {
      requestVersionRef.current += 1;
      setConversation({ assistantId: nextAssistantId });
      setMessages([]);
      setInput('');
      dispatch(setActiveHistoryId(null));
    }
  }, [assistants, conversation.assistantId, dispatch]);

  useEffect(() => {
    requestVersionRef.current += 1;
    dispatch(setActiveHistoryId(null));
    return () => {
      requestVersionRef.current += 1;
    };
  }, [dispatch, historyScope]);

  useEffect(() => {
    if (!visible) {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('chat') === '1') dispatch(toggleOpen());
    }
  }, [dispatch, visible]);

  useEffect(() => {
    if (visible && messages.length === 0 && !isAssistantsLoading) {
      const assistantId = resolveAssistantId(conversation.assistantId, assistants, fallbackAssistantId);
      if (assistants.length > 0 && conversation.assistantId !== assistantId) return;
      let isCurrentAssistant = true;

      const fetchWelcome = async () => {
        try {
          const { data } = await getWelcomeMessage(assistantId);

          if (isCurrentAssistant && data?.welcome_message) {
            setMessages([{ role: 'assistant', content: data.welcome_message }]);
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      };
      fetchWelcome();

      return () => {
        isCurrentAssistant = false;
      };
    }
  }, [assistants, conversation.assistantId, fallbackAssistantId, getWelcomeMessage, isAssistantsLoading, messages.length, visible]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const resize = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = size.w;
    const startHeight = size.h;
    let currentWidth = startWidth;
    let currentHeight = startHeight;

    const handleMove = (moveEvent: MouseEvent) => {
      const maxWidth = Math.round(window.innerWidth * 0.95);
      const maxHeight = Math.round(window.innerHeight * 0.9);
      currentWidth = clamp(startWidth + startX - moveEvent.clientX, MIN_WIDTH, maxWidth);
      currentHeight = clamp(startHeight + startY - moveEvent.clientY, MIN_HEIGHT, maxHeight);
      setLocalSize({ w: currentWidth, h: currentHeight });
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleMouseUp);
      dispatch(setSize({ w: currentWidth, h: currentHeight }));
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const invalidatePendingRequest = useCallback(() => {
    requestVersionRef.current += 1;
  }, []);

  const handleNewChat = useCallback(() => {
    invalidatePendingRequest();
    setMessages([]);
    setInput('');
    dispatch(setActiveHistoryId(null));
  }, [dispatch, invalidatePendingRequest]);

  const sendMessage = useCallback(async () => {
    const message = input.trim();
    if (!message || sending || isAssistantsLoading) return;

    setInput('');

    const conversationId = activeHistoryId ?? crypto.randomUUID();
    const requestVersion = requestVersionRef.current;
    const nextMessages: MessageType[] = [...messages, { role: 'user', content: message }];
    const updateConversation = (updatedMessages: MessageType[]) => {
      const chat: ChatHistory = {
        id: conversationId,
        scope: assistantHistoryScope,
        assistantId: historyAssistantId,
        messages: updatedMessages,
        timestamp: Date.now(),
      };
      setMessages(updatedMessages);
      dispatch(saveConversation(chat));
    };
    updateConversation(nextMessages);

    try {
      const assistantId = resolveAssistantId(conversation.assistantId, assistants, fallbackAssistantId);
      const request = assistantId === undefined ? { message, history: messages } : { message, history: messages, assistantId };
      const { message: assistantText = '' } = await sendChatMessage(request).unwrap();

      if (requestVersion !== requestVersionRef.current) return;
      updateConversation([...nextMessages, { role: 'assistant', content: assistantText }]);
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) return;
      const detail = error instanceof Error ? error.message : (getNestedErrorDetail(error) ?? t('user.chat.connectionError'));
      updateConversation([...nextMessages, { role: 'assistant', content: `❌ ${detail}` }]);
    }
  }, [
    activeHistoryId,
    assistantHistoryScope,
    historyAssistantId,
    assistants,
    conversation.assistantId,
    dispatch,
    fallbackAssistantId,
    input,
    isAssistantsLoading,
    messages,
    sendChatMessage,
    sending,
    t,
  ]);

  const copyToClipboard = useCallback(async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), COPY_FEEDBACK_DURATION_MS);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);

  const handleToggleOpen = useCallback(() => {
    dispatch(toggleOpen());
  }, [dispatch]);

  const handleAssistantChange = useCallback(
    (assistantId: string | null) => {
      setConversation({ assistantId });
      handleNewChat();
    },
    [handleNewChat]
  );

  return (
    <>
      <StyledChatIconButton onClick={handleToggleOpen} title={t('user.chat.assistantTitle')} data-testid={toggleButtonTestId}>
        <SmartToy sx={{ fontSize: '25px' }} />
      </StyledChatIconButton>
      <Dialog
        open={visible}
        hideBackdrop
        disableEnforceFocus
        disablePortal={false}
        fullWidth
        maxWidth={false}
        fullScreen={fullScreen}
        size={localSize}>
        {!fullScreen && <Resizer onMouseDown={resize} />}
        <Header
          historyScope={assistantHistoryScope}
          messages={messages}
          setMessages={setMessages}
          setConversation={setConversation}
          onNewChat={handleNewChat}
          onConversationChange={invalidatePendingRequest}
        />
        <Messages>
          {messages.map((message, index) => (
            <Message
              key={`${message.role}-${message.content}-${index}`}
              role={message.role}
              content={message.content}
              isCopied={copiedIndex === index}
              onCopy={(text) => copyToClipboard(text, index)}
            />
          ))}
          {sending && <Message role="assistant" isThinking />}
          <div ref={bottomRef} />
        </Messages>
        <Footer
          assistants={assistants}
          selectedAssistantId={conversation.assistantId}
          showAssistantPicker={showAssistantPicker}
          isInteractionDisabled={isAssistantsLoading}
          onAssistantChange={handleAssistantChange}
          input={input}
          onInputChange={setInput}
          sending={sending}
          onSend={sendMessage}
        />
      </Dialog>
    </>
  );
};

export const ChatbotWidget = memo(ChatbotWidgetBase);
